import React, { useEffect, useRef, useState } from 'react';

interface Props {
  value?: string;
  placeholder?: string;
  onAddressChange?: (value: string) => void;
  onPlaceSelected?: (address: string, lat: number | null, lng: number | null) => void;
}



// Google Places Autocomplete (new API): PlaceAutocompleteElement
// - Restricted to Hungary
// - Requires NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
const GooglePlacesAutocomplete: React.FC<Props> = ({
  value = '',
    placeholder = 'Search address... (Hungary)',
  onAddressChange,
  onPlaceSelected,
}) => {

  const hostRef = useRef<HTMLDivElement | null>(null);
  const elementRef = useRef<any>(null);
  const [showGhost, setShowGhost] = useState<boolean>(false);


  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onReady = () => {
      const g: any = (window as any).google;
      if (!g?.maps?.places || !hostRef.current) return;

      // If element already exists, reuse it
      if (!elementRef.current) {
        // Create the PlaceAutocompleteElement (new Maps JS API)
        const el: any = new g.maps.places.PlaceAutocompleteElement();

        el.setAttribute('id', `place-autocomplete-${Math.random().toString(36).slice(2)}`);
        // Restrict to Hungary
        try {
          // Restrict predictions to Hungary using the new API option
          (el as any).includedRegionCodes = ['hu', 'ae'];
        } catch {}
        // Placeholder text
        try { el.placeholder = placeholder; } catch {}

        // Initial value text (string)
        try {
          (el as any).value = value || '';
          if ('valueText' in el) (el as any).valueText = value || '';
          el.setAttribute('value', value || '');
        } catch {}


        // Listen for selection using the new gmp-select event
        const onPlaceSelect = async (e: any) => {
          try {
            const prediction = e?.detail?.placePrediction || e?.placePrediction;
            if (!prediction) { return; }
            const place = prediction.toPlace();
            await place.fetchFields({ fields: ['formattedAddress', 'location'] });
            const addr: string = place.formattedAddress || '';
            const loc: any = place.location;
            const lat = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
            const lng = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
            onAddressChange && onAddressChange(addr);
            onPlaceSelected && onPlaceSelected(addr, lat, lng);
          } catch {}
        };
        el.addEventListener('gmp-select', onPlaceSelect as any);

        // Reflect text changes to parent when typing
        const onInput = (e?: any) => {
          const t = e?.target?.value || e?.target?.valueText || (el as any).value || (el as any).valueText || '';
          onAddressChange && onAddressChange(String(t));
        };
        el.addEventListener('input', onInput);
        el.addEventListener('change', onInput as any);




        elementRef.current = el;
        // Ensure visible sizing
        try { el.style.display = 'block'; el.style.width = '100%'; } catch {}
        if (!hostRef.current.contains(el)) {
          hostRef.current.innerHTML = '';
          hostRef.current.appendChild(el);
        }
      }

      // Keep value in sync when prop changes
      const el = elementRef.current as any;
      if (el) {
        const apply = () => {
          try {
            if (el.value !== value) el.value = value || '';
            if ('valueText' in el && el.valueText !== value) el.valueText = value || '';
            el.setAttribute('value', value || '');
          } catch {}
        };
        apply();
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(apply);
        setTimeout(apply, 50);
      }
    };

    // If already loaded, init immediately
    if ((window as any).google?.maps?.places) { onReady(); }

    // Show ghost input if we have a value but the element won't reflect it
    setShowGhost(Boolean(value));

    // Otherwise inject script
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return; // fall back silently

    // Avoid duplicate loads
    const existing = document.querySelector('script[data-role="gmaps-places"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => { onReady(); });
    } else {
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=hu&v=weekly`;
      s.async = true;
      s.defer = true;
      s.dataset.role = 'gmaps-places';
      s.addEventListener('load', () => { onReady(); });
      document.head.appendChild(s);
    }

    return () => {};
  }, [value, placeholder]);

  return (
    <div className="w-full relative border-1">
      {/* Host for the Google element */}
      <div ref={hostRef} className="w-full" />

      {/* Ghost input to display existing address when the widget won't prefill */}
      {showGhost && value ? (
        <input
          type="text"
          className="absolute inset-0 w-full h-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={value}
          readOnly
          onFocus={() => {
            setShowGhost(false);
            try { (elementRef.current as any)?.focus?.(); } catch {}
          }}
          onClick={() => {
            setShowGhost(false);
            try { (elementRef.current as any)?.focus?.(); } catch {}
          }}
        />
      ) : null}
    </div>
  );
};

export default GooglePlacesAutocomplete;
